import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      const to = loc.state?.from || (u.role === "admin" ? "/admin" : u.access_status === "active" ? "/dashboard" : "/paywall");
      nav(to, { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1766297246931-7b861269dab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwzfHxjbGluaWNhbCUyMGxhYm9yYXRvcnklMjBtaWNyb3Njb3BlfGVufDB8fHx8MTc4ODA3OTEyOHww&ixlib=rb-4.1.0&q=85"
          alt="Clinical laboratory"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative h-full flex flex-col justify-between p-10 text-white">
          <div className="flex items-center gap-2">
            <Activity className="h-5 w-5" strokeWidth={1.75} />
            <span className="font-heading font-extrabold tracking-tight">RIF Master</span>
          </div>
          <div>
            <div className="eyebrow text-slate-300">Clinical decision support</div>
            <p className="mt-2 text-3xl font-heading font-extrabold tracking-tight leading-tight">
              Every protocol dosed. Every reference cited.
            </p>
            <p className="mt-3 text-sm text-slate-300 max-w-md">
              Sign in to continue your RIF cases, export patient-ready PDFs, and browse the curated research library.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 bg-white">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Activity className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
            <span className="font-heading font-extrabold">RIF Master</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Sign in</h1>
          <p className="mt-2 text-sm text-slate-600">Welcome back. Use the account you registered with.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-800" data-testid="login-submit-btn">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600">
            Don’t have an account? <Link to="/register" className="text-blue-900 font-semibold hover:underline" data-testid="link-to-register">Create one</Link>
          </div>
          <Link to="/" className="mt-4 inline-block text-xs text-slate-500 hover:text-slate-700">← Back to homepage</Link>
        </div>
      </div>
    </div>
  );
}
